// Turns raw backend JSON into the typed model. The parsers are tolerant: one
// malformed item is dropped (and logged) instead of failing the whole payload.
// A payload of the wrong overall shape throws, so callers can treat it as a
// failed request and keep their last good data.

import {
    bool, collectionEntries, isRecord, num, optNum, optStr, str, toSeconds, warnDropped, type Json,
} from './coerce';
import type {
    Division, Game, Goal, GoalieGameScore, GoalieLatestGame, LocalizedName, Player, PlayerNews,
    PointsByDate, SkaterLatestGame, TeamStanding, VersionInfo, WhatsNewItem,
} from './types';

const requireCollection = (data: unknown, what: string) => {
    if (!Array.isArray(data) && !isRecord(data)) {
        throw new TypeError(`Unexpected ${what} payload`);
    }
};

const localizedName = (value: unknown): LocalizedName | undefined => {
    if (typeof value === 'string') {
        return { default: value };
    }
    if (isRecord(value) && typeof value.default === 'string') {
        return { default: value.default, fi: optStr(value.fi) };
    }
    return undefined;
};

const skaterLatestGame = (raw: unknown): SkaterLatestGame | undefined => {
    if (!isRecord(raw)) {
        return undefined;
    }
    return {
        date: str(raw.date),
        goals: num(raw.goals),
        assists: num(raw.assists),
        points: num(raw.points, num(raw.goals) + num(raw.assists)),
        timeOnIce: str(raw.timeOnIce, '00:00'),
        shots: num(raw.shots),
        plusMinus: num(raw.plusMinus),
        hits: num(raw.hits),
        pim: num(raw.pim),
    };
};

const goalieLatestGame = (raw: unknown): GoalieLatestGame | undefined => {
    if (!isRecord(raw)) {
        return undefined;
    }
    return {
        date: str(raw.date),
        saves: num(raw.saves),
        shotsAgainst: num(raw.shotsAgainst),
        savePct: num(raw.savePct),
        toi: str(raw.timeOnIce, '00:00'),
    };
};

const parsePlayer = (raw: Json): Player => {
    const firstName = str(raw.firstName);
    const lastName = str(raw.lastName);
    const base = {
        playerId: raw.playerId as number | string,
        isActive: bool(raw.isActive),
        firstName,
        lastName,
        displayName: `${firstName} ${lastName}`,
        primaryPosition: str(raw.primaryPosition),
        age: num(raw.age),
        nationality: str(raw.nationality),
        team: str(raw.team),
        teamId: num(raw.teamId),
        games: num(raw.games),
        timeOnIcePerGame: toSeconds(raw.timeOnIcePerGame),
        injury: num(raw.injury),
        sweaterNumber: optNum(raw.sweaterNumber),
        contract: optStr(raw.contract) ?? (typeof raw.contract === 'number' ? String(raw.contract) : undefined),
        capHit: optNum(raw.capHit),
    };

    if (raw.primaryPosition === 'G') {
        return {
            ...base,
            kind: 'goalie',
            saves: num(raw.saves),
            shotsAgainst: num(raw.shotsAgainst),
            goalsAgainst: num(raw.goalsAgainst),
            savePct: num(raw.savePct),
            gaa: num(raw.gaa),
            wins: num(raw.wins),
            losses: num(raw.losses),
            ot: num(raw.ot),
            espnId: optNum(raw.espnId),
            latestGame: goalieLatestGame(raw.latestGame),
        };
    }

    const games = num(raw.games);
    const points = num(raw.points);
    const faceoffFraction = optNum(raw.faceoffPct);
    return {
        ...base,
        kind: 'skater',
        goals: num(raw.goals),
        assists: num(raw.assists),
        points,
        pointsPerGame: games > 0 ? points / games : 0,
        shotPct: num(raw.shotPct),
        plusMinus: num(raw.plusMinus),
        pim: num(raw.pim),
        hits: num(raw.hits),
        blocked: num(raw.blocked),
        ppToi: str(raw.ppToi, '00:00'),
        shToi: str(raw.shToi, '00:00'),
        faceoffPct: faceoffFraction === undefined ? undefined : faceoffFraction * 100,
        espnId: optNum(raw.espnId),
        latestGame: skaterLatestGame(raw.latestGame),
    };
};

// Players sorted by points, highest first (goalies, who have none, last).
export const parsePlayers = (data: unknown): Player[] => {
    requireCollection(data, 'players');

    const players: Player[] = [];
    for (const [key, item] of collectionEntries(data)) {
        if (!isRecord(item) || item.playerId === undefined || item.playerId === null || item.playerId === '') {
            warnDropped('player', key, 'not an object with a playerId');
            continue;
        }
        players.push(parsePlayer(item));
    }

    const points = (player: Player) => (player.kind === 'skater' ? player.points : -1);
    return players.sort((a, b) => points(b) - points(a));
};

const parseGoal = (raw: Json): Goal => ({
    time: str(raw.time, '00:00'),
    period: num(raw.period, 1),
    homeGoals: num(raw.homeGoals),
    awayGoals: num(raw.awayGoals),
    scorerName: localizedName(raw.scorerName),
    scorerId: (raw.scorerId ?? undefined) as Goal['scorerId'],
    assistOneName: localizedName(raw.assistOneName),
    assistOneId: (raw.assistOneId ?? undefined) as Goal['assistOneId'],
    assistTwoName: localizedName(raw.assistTwoName),
    assistTwoId: (raw.assistTwoId ?? undefined) as Goal['assistTwoId'],
    strength: optStr(raw.strength),
    isEmptyNet: bool(raw.isEmptyNet),
    isGameWinningGoal: bool(raw.isGameWinningGoal),
    type: optStr(raw.type),
    ordinalNum: optStr(raw.ordinalNum),
    videoUrl: optStr(raw.videoUrl),
});

const parseGoalie = (raw: Json): GoalieGameScore => {
    const firstName = str(raw.firstName);
    const lastName = str(raw.lastName);
    return {
        playerId: raw.playerId as GoalieGameScore['playerId'],
        firstName,
        lastName,
        displayName: `${firstName} ${lastName}`,
        team: str(raw.team),
        saves: num(raw.saves),
        shotsAgainst: num(raw.shotsAgainst),
        savePct: num(raw.savePct),
        timeOnIce: str(raw.timeOnIce, '00:00'),
    };
};

const records = (data: unknown): Json[] =>
    collectionEntries(data, { numericKeysOnly: false })
        .map(([, item]) => item)
        .filter(isRecord);

const parseGame = (raw: Json): Game => ({
    gameId: raw.gameId as Game['gameId'],
    isUpcoming: bool(raw.isUpcoming),
    date: str(raw.date),
    startTime: str(raw.startTime),
    homeTeam: str(raw.homeTeam),
    homeTeamId: num(raw.homeTeamId),
    awayTeam: str(raw.awayTeam),
    awayTeamId: num(raw.awayTeamId),
    homeTeamGoals: num(raw.homeTeamGoals),
    awayTeamGoals: num(raw.awayTeamGoals),
    currentPeriod: num(raw.currentPeriod),
    cpTimeRemaining: str(raw.cpTimeRemaining, '20:00'),
    extUrl: optStr(raw.extUrl),
    recapUrl: optStr(raw.recapUrl),
    status: str(raw.status),
    type: num(raw.type, 2),
    seriesStatus: optStr(raw.seriesStatus),
    gameNumber: optNum(raw.gameNumber),
    goals: records(raw.goals).map(parseGoal),
    goalies: records(raw.goalies).map(parseGoalie),
});

export const parseScores = (data: unknown): Game[] => {
    requireCollection(data, 'scores');

    const games: Game[] = [];
    for (const [key, item] of collectionEntries(data)) {
        if (!isRecord(item) || item.gameId === undefined || typeof item.date !== 'string') {
            warnDropped('game', key, 'not an object with a gameId and date');
            continue;
        }
        games.push(parseGame(item));
    }
    return games;
};

const parseTeam = (raw: Json, division: Json): TeamStanding => ({
    name: str(raw.name),
    abbrev: str(raw.abbrev),
    id: num(raw.id),
    games: num(raw.games),
    points: num(raw.points),
    wins: num(raw.wins),
    losses: num(raw.losses),
    ot: num(raw.ot),
    rw: num(raw.rw),
    row: num(raw.row),
    clinch: optStr(raw.clinch),
    divPos: num(raw.divPos),
    confPos: num(raw.confPos),
    wcPos: num(raw.wcPos),
    pointsPct: optNum(raw.pointsPct),
    divAbbrev: str(division.divAbbrev),
    confAbbrev: str(division.confAbbrev),
});

export const parseStandings = (data: unknown): Division[] => {
    requireCollection(data, 'standings');

    const divisions: Division[] = [];
    for (const [key, item] of collectionEntries(data)) {
        if (!isRecord(item) || !Array.isArray(item.teams)) {
            warnDropped('division', key, 'not an object with a teams list');
            continue;
        }
        divisions.push({
            name: str(item.name),
            abbrev: str(item.divAbbrev),
            teams: item.teams.filter(isRecord).map(team => parseTeam(team, item)),
        });
    }
    return divisions;
};

export const parsePoints = (data: unknown): PointsByDate => {
    if (!isRecord(data)) {
        throw new TypeError('Unexpected points payload');
    }

    const points: PointsByDate = {};
    for (const [date, value] of Object.entries(data)) {
        if (typeof value === 'number' && Number.isFinite(value)) {
            points[date] = value;
        } else {
            warnDropped('points day', date, 'not a number');
        }
    }
    return points;
};

export const parseVersionInfo = (data: unknown): VersionInfo => {
    if (!isRecord(data) || data.updateId === undefined || data.appVersion === undefined) {
        throw new TypeError('Unexpected versionInfo payload');
    }
    return {
        updateId: String(data.updateId),
        appVersion: num(data.appVersion),
        lastUpdated: typeof data.lastUpdated === 'number' || typeof data.lastUpdated === 'string'
            ? data.lastUpdated
            : undefined,
    };
};

// /api/ahl/updateId: versionInfo without the app version.
export const parseUpdateId = (data: unknown): Omit<VersionInfo, 'appVersion'> => {
    if (!isRecord(data) || data.updateId === undefined) {
        throw new TypeError('Unexpected updateId payload');
    }
    return {
        updateId: String(data.updateId),
        lastUpdated: typeof data.lastUpdated === 'number' || typeof data.lastUpdated === 'string'
            ? data.lastUpdated
            : undefined,
    };
};

// The newest item of an ESPN player news feed that isn't a "Story", or undefined.
export const parsePlayerNews = (data: unknown): PlayerNews | undefined => {
    if (!isRecord(data) || !Array.isArray(data.feed)) {
        throw new TypeError('Unexpected player news payload');
    }

    const item = data.feed.find(feed => isRecord(feed) && feed.type !== 'Story');
    if (!isRecord(item)) {
        return undefined;
    }
    const lastModified = new Date(str(item.lastModified));
    return {
        headline: str(item.headline),
        story: str(item.story),
        lastModified: Number.isNaN(lastModified.getTime()) ? new Date(0) : lastModified,
    };
};

// /api/whatsNew: the app's changes, newest (biggest id) first.
export const parseWhatsNew = (data: unknown): WhatsNewItem[] => {
    if (!Array.isArray(data)) {
        throw new TypeError('Unexpected whatsNew payload');
    }

    const items: WhatsNewItem[] = [];
    data.forEach((raw: unknown, index) => {
        if (!isRecord(raw) || !Number.isInteger(raw.id) || (raw.id as number) <= 0) {
            warnDropped('whatsNew item', String(index), 'no valid id');
            return;
        }
        const title = str(raw.title).trim();
        if (title === '') {
            warnDropped('whatsNew item', String(raw.id), 'no title');
            return;
        }
        items.push({ id: raw.id as number, date: str(raw.date), title, body: str(raw.body) });
    });
    return items.sort((a, b) => b.id - a.id);
};
